import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Component, inject, Inject, OnInit } from '@angular/core';
import { ECONode, Organization, OrgHat } from '@newgenus/common';
import { OrgHattingService } from '../../services/org-hatting.service';
import { NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

/**
 * Input data for the hat selection dialog.
 */
export interface SelectHatDialogInput {
    /** The target position node being dropped on */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    node: ECONode<any>;
    /** The user being assigned (from drag-drop) */
    user: { uid: string; name: string; email?: string };
    /** The organization */
    organization: Organization;
    /** Pre-filtered hats for this position (optional, will be computed if not provided) */
    hatsForPosition?: OrgHat[];
    /** The actual target position partKey (for nested parts like sections/units within a department node) */
    targetPartKey?: string;
    /** The actual target position name (for nested parts like sections/units within a department node) */
    targetPartName?: string;
}

/**
 * Result returned when dialog closes.
 */
export interface SelectHatDialogResult {
    /** Whether the user confirmed an action */
    confirmed: boolean;
    /** The action taken */
    action?: 'assign' | 'create-new' | 'cancel';
    /** The selected hat (if action is 'assign') - for single selection (deprecated) */
    selectedHat?: OrgHat;
    /** The selected hats (if action is 'assign') - supports multiple selection */
    selectedHats?: OrgHat[];
    /** The target position partKey (passed through for create-new flow) */
    targetPartKey?: string;
    /** The user being assigned (passed through for create-new flow) */
    user?: { uid: string; name: string; email?: string };
    /** Error message if something failed */
    error?: string;
    /** Whether the assignment is pending approval */
    pending?: boolean;
}

@Component({
    selector: 'shared-org-select-hat-dialog',
    imports: [
        NgFor,
        NgIf,
        TitleCasePipe,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
    ],
    template: `
        <h2 mat-dialog-title>
            <mat-icon class="title-icon">person_add</mat-icon>
            Assign {{ data.user.name | titlecase }} to Hat

            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content">
            <!-- Post info -->
            <div class="position-info">
                <strong>Post:</strong> {{ data.targetPartName || data.node.data?.name || 'Unknown Post' }}
            </div>

            <!-- Loading state -->
            <div *ngIf="isLoading" class="loading-container">
                <mat-spinner diameter="40"></mat-spinner>
                <span>Processing assignment...</span>
            </div>

            <!-- Hats available for this position -->
            <div *ngIf="!isLoading && hatsForPosition.length > 0" class="hats-section">
                <h3>
                    <mat-icon>work</mat-icon>
                    Available Hats ({{ hatsForPosition.length }})
                </h3>
                <p class="hint">Select one or more hats to assign {{ data.user.name | titlecase }} to:</p>

                <!-- Select All option -->
                <div class="select-all-row" *ngIf="getSelectableHats().length > 1">
                    <mat-checkbox
                        [checked]="allSelectableSelected()"
                        [indeterminate]="someSelected() && !allSelectableSelected()"
                        (change)="toggleSelectAll($event.checked)"
                    >
                        Select All ({{ getSelectableHats().length }})
                    </mat-checkbox>
                </div>

                <mat-selection-list [multiple]="true" class="hat-list" #hatList>
                    <mat-list-option
                        *ngFor="let hat of hatsForPosition"
                        [value]="hat"
                        [selected]="isHatSelected(hat)"
                        (selectedChange)="onHatSelectionChange(hat, $event)"
                        [class.user-already-assigned]="isUserInHat(hat)"
                        [disabled]="isUserInHat(hat)"
                    >
                        <div class="hat-option">
                            <div class="hat-info">
                                <mat-icon mat-list-icon>work_outline</mat-icon>
                                <span class="hat-name">{{ hat.name || 'Unnamed Hat' }}</span>
                                <span class="hat-pill">Hat</span>
                                <span *ngIf="hat.description" class="hat-description">{{ hat.description }}</span>
                            </div>
                            <div class="hat-meta">
                                <span class="user-count">
                                    <mat-icon>people</mat-icon>
                                    {{ getUserCount(hat) }} users
                                </span>
                                <span *ngIf="isUserInHat(hat)" class="already-assigned-badge"> Already assigned </span>
                            </div>
                        </div>
                    </mat-list-option>
                </mat-selection-list>
            </div>

            <!-- Special Rights Hats -->
            <div *ngIf="!isLoading && specialRightsHats.length > 0" class="hats-section special-rights-section">
                <h3>
                    <mat-icon>verified_user</mat-icon>
                    Special Rights Hats ({{ specialRightsHats.length }})
                </h3>
                <p class="hint">Org-wide hats granting additional permissions (not linked to any position):</p>

                <mat-selection-list [multiple]="true" class="hat-list">
                    <mat-list-option
                        *ngFor="let hat of specialRightsHats"
                        [value]="hat"
                        [selected]="isHatSelected(hat)"
                        (selectedChange)="onHatSelectionChange(hat, $event)"
                        [class.user-already-assigned]="isUserInHat(hat)"
                        [disabled]="isUserInHat(hat)"
                    >
                        <div class="hat-option">
                            <div class="hat-info">
                                <mat-icon mat-list-icon>verified_user</mat-icon>
                                <span class="hat-name">{{ hat.name || 'Unnamed Hat' }}</span>
                                <span class="hat-pill special">Special</span>
                                <span *ngIf="hat.description" class="hat-description">{{ hat.description }}</span>
                            </div>
                            <div class="hat-meta">
                                <span class="user-count">
                                    <mat-icon>people</mat-icon>
                                    {{ getUserCount(hat) }} users
                                </span>
                                <span *ngIf="isUserInHat(hat)" class="already-assigned-badge"> Already assigned </span>
                            </div>
                        </div>
                    </mat-list-option>
                </mat-selection-list>
            </div>

            <!-- No hats for this position -->
            <div
                *ngIf="!isLoading && hatsForPosition.length === 0 && specialRightsHats.length === 0"
                class="no-hats-section"
            >
                <mat-icon class="warning-icon">warning</mat-icon>
                <h3>No Hats Found</h3>
                <p>This position doesn't have any hats linked to it yet.</p>
                <p>Would you like to create a new hat for this position?</p>
            </div>

            <!-- Error display -->
            <div *ngIf="error" class="error-message">
                <mat-icon>error</mat-icon>
                {{ error }}
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close color="warn" [disabled]="isLoading">Cancel</button>

            <button mat-stroked-button color="accent" (click)="openCreateHatDialog()" [disabled]="isLoading">
                <mat-icon>add</mat-icon>
                Create New Hat
            </button>

            <button
                *ngIf="hasPositionHatSelected()"
                mat-raised-button
                color="primary"
                (click)="confirmAssignment()"
                [disabled]="isLoading"
            >
                <mat-icon>check</mat-icon>
                Assign to {{ selectedHats.length === 1 ? selectedHats[0].name : selectedHats.length + ' hats' }}
            </button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .title-icon {
                vertical-align: middle;
                margin-right: 8px;
            }

            .close-button {
                position: absolute;
                right: 8px;
                top: 8px;
            }

            .dialog-content {
                min-height: 200px;
                max-height: 60vh;
                overflow-y: auto;
                padding: 16px;
            }

            .position-info {
                background: rgba(0, 0, 0, 0.04);
                padding: 12px 16px;
                border-radius: 4px;
                margin-bottom: 16px;
            }

            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 40px;
                gap: 16px;
            }

            .hats-section,
            .no-hats-section {
                margin-top: 16px;
            }

            .special-rights-section {
                border-top: 1px solid rgba(0, 0, 0, 0.08);
                padding-top: 16px;
            }

            .hats-section h3,
            .no-hats-section h3 {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 8px;
            }

            .hint {
                color: rgba(0, 0, 0, 0.6);
                font-size: 14px;
                margin-bottom: 12px;
            }

            .select-all-row {
                padding: 8px 16px;
                background: rgba(0, 0, 0, 0.04);
                border-radius: 4px;
                margin-bottom: 8px;
            }

            .hat-list {
                max-height: 300px;
                overflow-y: auto;
            }

            .hat-option {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                padding: 8px 0;
            }

            .hat-info {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .hat-name {
                font-weight: 500;
            }

            .hat-pill {
                background: #e3f2fd;
                color: #1565c0;
                padding: 1px 8px;
                border-radius: 12px;
                font-size: 11px;
                font-weight: 500;
                white-space: nowrap;
            }

            .hat-pill.special {
                background: #f3e5f5;
                color: #7b1fa2;
            }

            .hat-description {
                color: rgba(0, 0, 0, 0.6);
                font-size: 12px;
                margin-left: 8px;
            }

            .hat-meta {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .user-count {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.6);
            }

            .user-count mat-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
            }

            .already-assigned-badge {
                background: #ff9800;
                color: white;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 11px;
            }

            .user-already-assigned {
                opacity: 0.6;
            }

            .no-hats-section {
                text-align: center;
                padding: 24px;
            }

            .warning-icon {
                font-size: 48px;
                width: 48px;
                height: 48px;
                color: #ff9800;
            }

            .error-message {
                display: flex;
                align-items: center;
                gap: 8px;
                color: #f44336;
                background: rgba(244, 67, 54, 0.1);
                padding: 12px 16px;
                border-radius: 4px;
                margin-top: 16px;
            }

            mat-dialog-actions {
                padding: 16px;
                gap: 8px;
            }
        `,
    ],
})
export class OrgSelectHatDialogComponent implements OnInit {
    public hatsForPosition: OrgHat[] = [];
    public specialRightsHats: OrgHat[] = [];
    public selectedHats: OrgHat[] = [];
    public isLoading = false;
    public error: string | null = null;

    private orgHattingService = inject(OrgHattingService);

    constructor(
        public dialogRef: MatDialogRef<OrgSelectHatDialogComponent, SelectHatDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: SelectHatDialogInput
    ) {}

    ngOnInit(): void {
        // Use pre-computed hats if provided, otherwise compute them
        if (this.data.hatsForPosition) {
            this.hatsForPosition = this.data.hatsForPosition;
        } else {
            this.loadHatsForPosition();
        }

        // Load special rights hats (exclude any already in hatsForPosition)
        const positionHatKeys = new Set(this.hatsForPosition.map((h) => h.key));
        this.specialRightsHats = this.orgHattingService
            .getSpecialRightsHats(this.data.organization)
            .filter((h) => !positionHatKeys.has(h.key));
    }

    private loadHatsForPosition(): void {
        // Use targetPartKey for nested parts (section/unit/post), fallback to node.data.partKey for departments
        const partKey = this.data.targetPartKey || this.data.node?.data?.partKey;
        if (!partKey) {
            this.hatsForPosition = [];
            return;
        }

        this.hatsForPosition = this.orgHattingService.getHatsForPosition(this.data.organization, partKey);
    }

    /**
     * Get hats that can be selected (user not already assigned)
     */
    public getSelectableHats(): OrgHat[] {
        return [...this.hatsForPosition, ...this.specialRightsHats].filter((hat) => !this.isUserInHat(hat));
    }

    /**
     * Check if a hat is currently selected
     */
    public isHatSelected(hat: OrgHat): boolean {
        return this.selectedHats.some((h) => h.key === hat.key);
    }

    /**
     * Handle selection change for a hat
     */
    public onHatSelectionChange(hat: OrgHat, selected: boolean): void {
        if (this.isUserInHat(hat)) {
            return; // Don't allow selection if user already assigned
        }

        if (selected) {
            if (!this.isHatSelected(hat)) {
                this.selectedHats = [...this.selectedHats, hat];
            }
        } else {
            this.selectedHats = this.selectedHats.filter((h) => h.key !== hat.key);
        }
    }

    /**
     * Check if all selectable hats are selected
     */
    public allSelectableSelected(): boolean {
        const selectable = this.getSelectableHats();
        return selectable.length > 0 && selectable.every((hat) => this.isHatSelected(hat));
    }

    /**
     * Check if some (but not all) hats are selected
     */
    public someSelected(): boolean {
        return this.selectedHats.length > 0;
    }

    /**
     * At least one position hat must be selected before assignment is allowed.
     * Special rights hats alone are not sufficient.
     */
    public hasPositionHatSelected(): boolean {
        const positionHatKeys = new Set(this.hatsForPosition.map((h) => h.key));
        return this.selectedHats.some((h) => positionHatKeys.has(h.key));
    }

    /**
     * Toggle select all functionality
     */
    public toggleSelectAll(selectAll: boolean): void {
        if (selectAll) {
            this.selectedHats = [...this.getSelectableHats()];
        } else {
            this.selectedHats = [];
        }
    }

    public selectHat(hat: OrgHat): void {
        if (this.isUserInHat(hat)) {
            return; // Don't select if user already assigned
        }
        // Toggle selection for single click
        if (this.isHatSelected(hat)) {
            this.selectedHats = this.selectedHats.filter((h) => h.key !== hat.key);
        } else {
            this.selectedHats = [...this.selectedHats, hat];
        }
    }

    public isUserInHat(hat: OrgHat): boolean {
        return this.orgHattingService.isUserInHat(hat, this.data.user.uid);
    }

    public getUserCount(hat: OrgHat): number {
        // Use assignedToArray as the canonical source for user count
        const assignedUsers = hat.assignedToArray || [];
        return assignedUsers.length;
    }

    public async confirmAssignment(): Promise<void> {
        if (this.selectedHats.length === 0) {
            return;
        }

        this.isLoading = true;
        this.error = null;

        try {
            const orgId = this.data.organization.key;
            const positionKey = this.data.targetPartKey || this.data.node?.data?.partKey || '';
            const positionName = this.data.targetPartName || this.data.node?.data?.name || '';
            const hatKeys = this.selectedHats.map((h) => h.key);
            const userId = this.data.user.uid;

            // Determine special rights hat keys for the approval record
            const positionHatKeys = new Set(this.hatsForPosition.map((h) => h.key));
            const specialRightsHatKeys = hatKeys.filter((k) => !positionHatKeys.has(k));

            // Check if this organization requires approval for hat assignments
            const requireApproval = !!this.data.organization.settings?.requireHatAssignmentApproval;

            // Use assignUserToPosition which handles approval-required vs direct assignment
            const result = await this.orgHattingService.assignUserToPosition(
                orgId,
                positionKey,
                positionName,
                hatKeys,
                userId,
                requireApproval,
                specialRightsHatKeys.length > 0 ? specialRightsHatKeys : undefined
            );

            if (result.succeeded) {
                this.dialogRef.close({
                    confirmed: true,
                    action: 'assign',
                    selectedHat: this.selectedHats[0],
                    selectedHats: this.selectedHats,
                    pending: result.pending,
                });
            } else {
                this.error = result.error || 'Assignment failed';
                this.isLoading = false;
            }
        } catch (err) {
            console.error('[OrgSelectHatDialog] Error assigning user:', err);
            this.error = (err as Error).message || 'An unexpected error occurred';
            this.isLoading = false;
        }
    }

    public openCreateHatDialog(): void {
        // Close this dialog and pass position + user data so the caller can open the create hat dialog
        // (Opening a new dialog from here while closing causes race conditions)
        this.dialogRef.close({
            confirmed: true,
            action: 'create-new',
            targetPartKey: this.data.targetPartKey,
            user: this.data.user,
        });
    }
}
